import type React from 'react';
// Asset imports - bundler handles path resolution
import { useState } from 'react';
import headerLogo from '~/static/assets/logos/headerLogo.png';
import {
  type CompanyInfo,
  companyInfo as defaultCompanyInfo,
  linkCategories as defaultLinkCategories,
  socialLinks as defaultSocialLinks,
  type FooterLink,
  footerText,
  type LinkCategory,
  type SocialLink,
} from '../../config';
import { JAAS_RELEASE } from '../../utils/jitsiAPIUtils';
import { FooterBackground } from './FooterBackground';

interface FooterProps {
  companyInfo?: CompanyInfo;
  linkCategories?: LinkCategory[];
  socialLinks?: SocialLink[];
  connectTitle?: string;
  connectDescription?: string;
}

declare global {
  interface Window {
    APP_REVISION?: string;
  }
}

// Footer configuration is now imported from footerConfig.ts

export const Footer: React.FC<FooterProps> = ({
  companyInfo: _companyInfo = defaultCompanyInfo,
  linkCategories = defaultLinkCategories,
  socialLinks = defaultSocialLinks,
  connectTitle: _connectTitle = footerText.connectTitle,
  connectDescription: _connectDescription = footerText.connectDescription,
}) => {
  // Footer uses footer_bg.png as background image for visual appeal
  const handleLinkClick = (link: FooterLink) => {
    if (link.external) {
      window.open(link.url, '_blank', 'noopener,noreferrer');
    } else if (link.url.startsWith('#')) {
      const element = document.querySelector(link.url);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      window.location.href = link.url;
    }
  };

  const handleSocialClick = (socialLink: SocialLink) => {
    if (socialLink.url.startsWith('tel:')) {
      window.location.href = socialLink.url;
    } else {
      window.open(socialLink.url, '_blank', 'noopener,noreferrer');
    }
  };

  const [lastText, setLastText] = useState(`© ${new Date().getFullYear()} ALL RIGHTS RESERVED`);

  const renderLinkCategory = (category: LinkCategory) => {
    return (
      <nav
        key={category.title}
        className="footer-link-category"
        aria-labelledby={`footer-nav-${category.title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <h3
          id={`footer-nav-${category.title.toLowerCase().replace(/\s+/g, '-')}`}
          className="footer-category-title"
        >
          {category.title}
        </h3>
        <ul className="footer-links-list">
          {category.links.map((link) => (
            <li key={link.label} className="footer-link-item">
              <button
                type="button"
                className="footer-link"
                onClick={() => handleLinkClick(link)}
                aria-label={link.external ? `${link.label} (opens in new tab)` : link.label}
              >
                {link.label}
                {link.external && (
                  <svg
                    className="footer-external-icon"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden="true"
                  >
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                    <polyline points="15,3 21,3 21,9" />
                    <line x1="10" y1="14" x2="21" y2="3" />
                  </svg>
                )}
              </button>
            </li>
          ))}
        </ul>
      </nav>
    );
  };

  const renderSocialLinks = () => {
    const getSocialIcon = (platform: string) => {
      switch (platform) {
        case 'Phone':
          return (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="footer-social-icon"
              aria-hidden="true"
            >
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
            </svg>
          );
        case 'Facebook':
          return (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="footer-social-icon"
              aria-hidden="true"
            >
              <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
            </svg>
          );
        case 'Twitter':
          return (
            <svg
              className="footer-social-icon"
              xmlns="http://www.w3.org/2000/svg"
              width="28"
              height="28"
              shapeRendering="geometricPrecision"
              textRendering="geometricPrecision"
              imageRendering="optimizeQuality"
              fillRule="evenodd"
              clipRule="evenodd"
              viewBox="0 0 512 462.799"
              aria-hidden="true"
            >
              <path
                fill="currentColor"
                d="M403.229 0h78.506L310.219 196.04 512 462.799H354.002L230.261 301.007 88.669 462.799h-78.56l183.455-209.683L0 0h161.999l111.856 147.88L403.229 0zm-27.556 415.805h43.505L138.363 44.527h-46.68l283.99 371.278z"
              />
            </svg>
          );
        case 'YouTube':
          return (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="footer-social-icon"
              aria-hidden="true"
            >
              <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z" />
              <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02" />
            </svg>
          );
        case 'LinkedIn':
          return (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="footer-social-icon"
              aria-hidden="true"
            >
              <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
              <rect x="2" y="9" width="4" height="12" />
              <circle cx="4" cy="4" r="2" />
            </svg>
          );
        default:
          return null;
      }
    };

    return (
      <nav className="footer-social-links" aria-labelledby="social-links-title">
        <ul className="footer-social-icons">
          {socialLinks.map((social) => (
            <li
              key={social.platform}
              className={social.platform === 'Phone' ? 'footer-social-phone-only' : ''}
            >
              <button
                type="button"
                className="footer-social-link"
                onClick={() => handleSocialClick(social)}
                aria-label={social.label}
                title={social.label}
              >
                {getSocialIcon(social.platform)}
              </button>
            </li>
          ))}
        </ul>
      </nav>
    );
  };

  const revision = window?.APP_REVISION ?? 'not specified';
  const release = JAAS_RELEASE ?? 'not specified';

  return (
    <footer className="footer">
      {/* Animated canvas background */}
      <div className="footer-animated-bg-container">
        <FooterBackground />
        <div className="footer-bg-overlay" />
      </div>
      <div className="footer-container">
        <div className="footer-content-wrapper">
          {/* Main Content Section */}
          <div className="footer-main-content">
            {/* Title Row - Full Width */}
            <h2 id="footer-title" className="footer-connect-title">
              <span className="footer-title-line-1">How can we help?</span>
              <span className="footer-title-line-2">Let's connect</span>
            </h2>

            {/* Content Row - Description + Link Columns */}
            <div className="footer-content-row">
              {/* Left - Description */}
              <div className="footer-description-column">
                {/* Design update (#13): "Sangoma" text is bold */}
                <p className="footer-company-description">
                  <strong>{footerText.companyName}</strong>
                  {footerText.companyDescriptionBody}
                </p>
              </div>

              {/* Right - Link Categories */}
              <nav className="footer-links-grid" aria-label="Footer navigation">
                {linkCategories.map((category) => renderLinkCategory(category))}
              </nav>
            </div>
          </div>

          {/* Bottom Section */}
          <div className="footer-bottom-section">
            <div className="footer-bottom-content">
              {/* Left - Sangoma Logo */}
              <div className="footer-sangoma-branding">
                <a
                  href="https://sangoma.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Visit Sangoma main website"
                >
                  <img
                    src={headerLogo.src}
                    width={headerLogo.width}
                    height={headerLogo.height}
                    alt="Sangoma company logo"
                    className="footer-sangoma-logo"
                    onError={(e) => {
                      // Fallback to text if logo fails to load
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        const fallbackText = document.createElement('span');
                        fallbackText.textContent = 'Sangoma';
                        fallbackText.className = 'footer-sangoma-text';
                        fallbackText.setAttribute('role', 'img');
                        fallbackText.setAttribute('aria-label', 'Sangoma company logo');
                        parent.appendChild(fallbackText);
                      }
                    }}
                  />
                </a>
              </div>

              {/* Center - Social Links */}
              {renderSocialLinks()}

              {/* Right - Copyright */}
              {/* biome-ignore lint/a11y/noStaticElementInteractions: Not for user interaction */}
              {/* biome-ignore lint/a11y/useKeyWithClickEvents: Not for user interaction */}
              <div
                className="footer-copyright"
                onClick={() => setLastText(`Revision ${revision}, ${release}`)}
              >
                <p className="footer-copyright-text">{lastText}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
